import api from './api';

/**
 * Contract Service
 * API client for the first-class `contracts` entity: AI drafting, versioned
 * drafts, contractor send, client signature + execution, void, and PDF.
 * All backend responses are { success, data } — unwrap() returns the data.
 */
const unwrap = (response) => response?.data?.data ?? response?.data;

const contractService = {
  // AI-draft a contract body for a bid. Returns { body, metadata } — NOT persisted.
  generate: async (bidId, commercialTerms) =>
    unwrap(await api.post('/contracts/generate', { bidId, commercialTerms })),

  // Create a draft contract. payload: { bidId, body, aiGenerated, aiModel, aiTokensUsed, aiPrompt }
  create: async (payload) => unwrap(await api.post('/contracts', payload)),

  // All contract versions for a bid (desc). Party-only.
  listForBid: async (bidId) => unwrap(await api.get(`/contracts/bid/${bidId}`)),

  getById: async (id) => unwrap(await api.get(`/contracts/${id}`)),

  // Draft-only body edit.
  updateBody: async (id, body) => unwrap(await api.put(`/contracts/${id}`, { body })),

  // Contractor signs + draft -> sent.
  send: async (id) => unwrap(await api.post(`/contracts/${id}/send`)),

  // Client signs; sent -> executed; generates PDF.
  sign: async (id, signatureId) => unwrap(await api.post(`/contracts/${id}/sign`, { signatureId })),

  // Contractor voids a draft|sent contract.
  voidContract: async (id) => unwrap(await api.post(`/contracts/${id}/void`)),

  // Fetch the PDF as a blob (so the Bearer token is sent) and return an object
  // URL the caller can open in a new tab. Available once sent/executed.
  fetchPdfObjectUrl: async (id) => {
    const response = await api.get(`/contracts/${id}/pdf`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  },
};

export default contractService;
